/* eslint-disable unicorn/consistent-function-scoping */
import { OverlayModule } from '@angular/cdk/overlay';
import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  Input,
  OnInit,
  Output,
  computed,
  inject,
  signal,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormControl, ReactiveFormsModule } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { selectIsRemoteAccessEnabled, selectIsUserSiteOffline, selectRecentPatients, selectUserSiteId } from '@libs/store';
import { AvatarsComponent, InputComponent } from '@libs/ui-lib';
import { routes } from '@libs/utils';
import { SvgIconComponent } from '@ngneat/svg-icon';
import { Store } from '@ngrx/store';
import { TranslateModule } from '@ngx-translate/core';
import { debounceTime } from 'rxjs';
import { PushTagOnClickDirective, TagEvent } from '@libs/shared-services';

const SEARCH_DEBOUNCE_TIME = 300;
const MAX_RECENT_PATIENTS = 15;

@Component({
  selector: 'lib-patient-search',
  templateUrl: 'patient-search.component.html',
  styleUrls: ['patient-search.component.scss'],
  imports: [
    ReactiveFormsModule,
    SvgIconComponent,
    InputComponent,
    TranslateModule,
    OverlayModule,
    RouterModule,
    AvatarsComponent,
    PushTagOnClickDirective,
  ],
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PatientSearchComponent implements OnInit {
  @Input() public totalCount: number = 0;
  @Input() public isLoading: boolean = false;
  @Input() public set searchTerm(searchTerm: string | null) {
    this.searchTermSignal.set(searchTerm ?? '');
    this.searchControl.setValue(searchTerm, { emitEvent: false });
  }

  @Output() public searchTermChanged = new EventEmitter<string>();

  private readonly destroyRef = inject(DestroyRef);
  private readonly store = inject(Store);

  protected searchTermSignal = signal('');
  protected recentPatientsDropdownIsOpen = signal(false);

  private readonly selectedSiteId = this.store.selectSignal(selectUserSiteId);
  protected readonly recentPatients = computed(() => this.store.selectSignal(selectRecentPatients)().slice(0, MAX_RECENT_PATIENTS));

  protected readonly searchControl = new FormControl<string | undefined>(undefined, { updateOn: 'change' });
  protected readonly TagEvent = TagEvent;
  protected routes = routes;

  public ngOnInit(): void {
    this.searchControl.valueChanges
      .pipe(takeUntilDestroyed(this.destroyRef), debounceTime(SEARCH_DEBOUNCE_TIME))
      .subscribe((searchTerm) => {
        this.searchTermSignal.set(searchTerm ?? '');
        return this.searchTermChanged.emit(searchTerm ?? '');
      });
  }

  protected clearSearchInput(): void {
    this.searchControl.setValue(null);
  }

  public openRecentPatientsDropdown(): void {
    const officeOffline = this.store.selectSignal(selectIsUserSiteOffline);
    const remoteAccessEnabled = this.store.selectSignal(selectIsRemoteAccessEnabled);
    if (this.recentPatients().length > 0 && !officeOffline() && remoteAccessEnabled()) {
      this.recentPatientsDropdownIsOpen.set(true);
    }
  }

  public closeRecentPatientsDropdown(): void {
    this.recentPatientsDropdownIsOpen.set(false);
  }
}
